import FollowUp from "../models/FollowUp";
import React, {useEffect, useRef} from "react";
import {formatDate, formatFollowUpTime} from "../utils/DateTimeUtils";
import {ContactOption} from "../models/enums/ContactOption";

function contactOptionIcon(option: ContactOption): string {
    switch (option) {
        case ContactOption.EMAIL:   return '✉️';
        case ContactOption.PHONE:   return '📞';
        case ContactOption.MEETING: return '🤝';
        default:                    return '📋';
    }
}

interface PopupProps {
    followUp: FollowUp;
    position: { top: number; left: number };
    onClose: () => void;
    onGoToClient: (clientId: number) => void;
}

const FollowUpCalendarPopup: React.FC<PopupProps> = ({ followUp, position, onClose, onGoToClient }) => {
    const popupRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (popupRef.current && !popupRef.current.contains(e.target as Node)) {
                onClose();
            }
        };
        // Small delay so the originating click doesn't immediately close the popup
        const timer = setTimeout(() => {
            document.addEventListener('mousedown', handleClickOutside);
        }, 50);
        return () => {
            clearTimeout(timer);
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [onClose]);

    // Clamp so the popup stays within the viewport
    const POPUP_WIDTH = 300;
    const clampedLeft = Math.min(position.left, window.innerWidth - POPUP_WIDTH - 16);
    const clampedTop = Math.min(position.top, window.innerHeight + window.scrollY - 280);

    return (
        <div
            ref={popupRef}
            className="fixed z-50 bg-white border border-gray-200 rounded-lg shadow-xl p-4"
            style={{ top: clampedTop, left: clampedLeft, width: POPUP_WIDTH }}
            data-testid='popup-wrapper'

        >
            {/* Header */}
            <div className="flex justify-between items-start mb-3">
                <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-crumbblue">Follow-Up</p>
                    <h3 className="text-base font-bold text-gray-800 leading-tight" data-testid="client-name">
                        {followUp.name ?? 'Client'}
                    </h3>
                </div>
                <button
                    onClick={onClose}
                    className="text-gray-400 hover:text-gray-600 text-xl leading-none ml-2 mt-0.5"
                    aria-label="Close"
                    data-testid='close-btn'
                >
                    &times;
                </button>
            </div>

            {/* Details */}
            <div className="space-y-2 text-sm text-gray-700 mb-4">
                <div className="flex items-center gap-2">
                    <span>📅</span>
                    <span data-testid="date">{formatDate(followUp.followUpDate)}</span>
                </div>
                {followUp.followUpTime && (
                    <div className="flex items-center gap-2">
                        <span>🕐</span>
                        <span data-testid='time'>
                            {formatFollowUpTime(followUp)}
                        </span>
                    </div>
                )}
                <div className="flex items-center gap-2">
                    <span>{contactOptionIcon(followUp.contactOption)}</span>
                    <span data-testid='contact'>{followUp.contactOption}</span>
                </div>
                {followUp.notes && (
                    <div className="flex items-start gap-2 pt-1 border-t border-gray-100">
                        <span className="mt-0.5">📝</span>
                        <span data-testid='note' className="text-gray-600 italic">
                            {followUp.notes.length > 120
                                ? followUp.notes.slice(0, 120) + '...'
                                : followUp.notes
                            }
                        </span>
                    </div>
                )}
            </div>

            {/* CTA */}
            <button
                onClick={() => onGoToClient(followUp.clientId)}
                className="w-full bg-crumbblue text-white text-sm font-medium py-2 rounded hover:bg-blue-700 transition"
                data-testid='view-client-btn'
            >
                View Client Page →
            </button>
        </div>
    );
};

export default FollowUpCalendarPopup;